import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, KeyRound, BarChart3, HeartPulse, Sparkles,
  FileBarChart, BellRing, GraduationCap, Settings, LogOut, ShieldCheck,
  Loader2,
} from "lucide-react";
import client, { endSession } from "../lib/api";
import iconMark from "../assets/brand/klareet-icon-white.png";

const SIDEBAR_BG = "#131822";
const HOVER_BG = "#1A202C";

const NAV = [
  { group: "Workspace", items: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, tid: "adminnav-overview" },
  ] },
  { group: "People", items: [
    { to: "/admin/roster", label: "Students", icon: Users, tid: "adminnav-roster" },
    { to: "/admin/seats", label: "Seat activation", icon: KeyRound, tid: "adminnav-seats" },
  ] },
  { group: "Analytics", items: [
    { to: "/admin/cohort", label: "Cohort view", icon: BarChart3, tid: "adminnav-cohort" },
    { to: "/admin/mood", label: "Mood pulse", icon: HeartPulse, tid: "adminnav-mood" },
  ] },
  { group: "Intelligence", items: [
    { to: "/admin/insights", label: "Klair insights", icon: Sparkles, tid: "adminnav-insights" },
    { to: "/admin/reports", label: "Reports", icon: FileBarChart, tid: "adminnav-reports" },
    { to: "/admin/triggers", label: "Triggers & alerts", icon: BellRing, tid: "adminnav-triggers" },
  ] },
  { group: "College", items: [
    { to: "/admin/college", label: "College page", icon: GraduationCap, tid: "adminnav-college" },
    { to: "/admin/settings", label: "Settings", icon: Settings, tid: "adminnav-settings" },
  ] },
];

function NavItem({ item, onNavigate }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={item.tid}
      className="group flex items-center gap-3 px-3 py-2 rounded-md text-sm mb-0.5"
      style={({ isActive }) => ({
        color: isActive ? "#F9F9F6" : "rgba(249,249,246,0.68)",
        background: isActive ? "rgba(29,95,212,0.22)" : "transparent",
        fontWeight: isActive ? 600 : 400,
        transition: "background-color 160ms ease, color 160ms ease",
      })}
      onMouseEnter={(e) => { if (!e.currentTarget.classList.contains("active")) e.currentTarget.style.background = HOVER_BG; }}
      onMouseLeave={(e) => {
        const active = e.currentTarget.getAttribute("aria-current") === "page";
        e.currentTarget.style.background = active ? "rgba(29,95,212,0.22)" : "transparent";
      }}
    >
      <Icon size={17} className="shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

export default function AdminShell({ children }) {
  const nav = useNavigate();
  const [state, setState] = useState("loading"); // loading | ok | denied
  const [org, setOrg] = useState(null);

  useEffect(() => {
    let done = false;
    client.get("/admin/overview")
      .then((r) => { if (!done) { setOrg(r?.data?.org || null); setState("ok"); } })
      .catch((e) => { if (!done) setState(e?.response?.status === 403 ? "denied" : "ok"); });
    return () => { done = true; };
  }, []);

  const signOut = async () => { try { await endSession(); } finally { nav("/"); } };

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F9F9F6" }} data-testid="admin-loading">
        <Loader2 className="animate-spin" size={22} style={{ color: SIDEBAR_BG }} />
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#F9F9F6" }}>
        <div className="k-card max-w-md w-full text-center" style={{ padding: "2.5rem" }} data-testid="admin-denied">
          <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(19,24,34,0.06)", color: SIDEBAR_BG }}>
            <ShieldCheck size={22} />
          </div>
          <h1 className="klareet-serif mb-2" style={{ fontSize: "1.4rem", color: "#131822" }}>Admin access only</h1>
          <p className="k-quiet mb-5" style={{ lineHeight: 1.55 }}>This area is for college administrators. Your account does not have admin access on this campus.</p>
          <button onClick={signOut} className="k-btn k-btn-primary" data-testid="admin-denied-signout">Sign out</button>
        </div>
      </div>
    );
  }

  const orgName = org?.display_name || "Your college";
  const logo = org?.logo_url || null;

  const sidebarInner = (
    <>
      <div className="flex items-center gap-2.5 px-2 pb-4">
        <div className="w-9 h-9 rounded-[11px] flex items-center justify-center shrink-0" style={{ background: "rgba(249,249,246,0.08)" }}>
          <img src={iconMark} alt="Klareet" className="w-5 h-5" />
        </div>
        <div className="leading-tight min-w-0">
          <div className="text-sm font-semibold" style={{ color: "#F9F9F6" }}>Klareet</div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "rgba(249,249,246,0.5)" }}>Admin</div>
        </div>
      </div>

      <div className="flex items-center gap-2.5 px-2 py-2.5 mb-3 rounded-lg" style={{ background: "rgba(249,249,246,0.05)" }} data-testid="admin-org-block">
        {logo ? (
          <img src={logo} alt={orgName} className="w-8 h-8 rounded object-contain bg-white p-0.5" data-testid="admin-org-logo" />
        ) : (
          <div className="w-8 h-8 rounded flex items-center justify-center text-xs font-semibold shrink-0" style={{ background: "#0E7A66", color: "white" }} data-testid="admin-org-logo">
            {orgName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide" style={{ color: "rgba(249,249,246,0.5)" }}>Campus</div>
          <div className="text-sm font-medium truncate" style={{ color: "#F9F9F6" }} data-testid="admin-org-name">{orgName}</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto" data-testid="admin-sidebar-nav">
        {NAV.map((section) => (
          <div key={section.group} className="mb-4">
            <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: "rgba(249,249,246,0.4)" }}>
              {section.group}
            </div>
            {section.items.map((it) => <NavItem key={it.to} item={it} />)}
          </div>
        ))}
      </nav>

      <button
        onClick={signOut}
        className="flex items-center gap-3 px-3 py-2 rounded-md text-sm mt-2"
        style={{ color: "rgba(249,249,246,0.7)", background: "transparent" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = HOVER_BG; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        data-testid="admin-signout"
      >
        <LogOut size={17} /> Sign out
      </button>
    </>
  );

  return (
    <div className="min-h-screen" style={{ background: "#F9F9F6" }} data-testid="admin-shell">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col fixed top-0 left-0 h-screen w-[260px] z-30 p-4"
        style={{ background: SIDEBAR_BG }}
      >
        {sidebarInner}
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 overflow-x-auto" style={{ background: SIDEBAR_BG }} data-testid="admin-mobile-nav">
        <img src={iconMark} alt="Klareet" className="w-5 h-5 shrink-0" />
        {NAV.flatMap((s) => s.items).map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={`${it.tid}-mobile`}
            className="px-3 py-1.5 rounded-full text-xs whitespace-nowrap shrink-0"
            style={({ isActive }) => ({
              color: isActive ? "#F9F9F6" : "rgba(249,249,246,0.65)",
              background: isActive ? "rgba(29,95,212,0.25)" : "rgba(249,249,246,0.06)",
            })}
          >
            {it.label}
          </NavLink>
        ))}
        <button onClick={signOut} className="px-3 py-1.5 rounded-full text-xs whitespace-nowrap shrink-0" style={{ color: "rgba(249,249,246,0.65)", background: "rgba(249,249,246,0.06)" }} data-testid="admin-signout-mobile">
          Sign out
        </button>
      </div>

      <main className="lg:ml-[260px] min-h-screen px-5 sm:px-8 py-8 max-w-[1400px]" data-testid="admin-main">
        {children}
      </main>
    </div>
  );
}
